
<script>
import { mapGetters, mapMutations, mapState } from 'vuex/dist/vuex.common';
import { wizardEmitterMixin } from '@/mixins/wizard';
import { LOADING_KEY } from '@/util/constants';

import EzButton from '@/components/ui/Button';
import EzWizard from '@/components/layout/Wizard.vue';
import EzLoader from '@/components/ui/Loader/EzLoader';
import steps, { RECIPE_STEP_1, RECIPE_STEP_2, RECIPE_STEP_3 } from './steps';

/**
 * Inventory Transfer Flow
 * @version 1.0.0
 * @since 3.18.0
 */

export default {
  mixins: [wizardEmitterMixin],
  components: { EzButton, EzWizard, EzLoader },
  data() {
    return {
      RECIPE_STEP_1,
      RECIPE_STEP_2,
      RECIPE_STEP_3,
      currentStep: 0,
      loadingMsg: 'Loading...',
      nextDisabled: true,
    };
  },
  computed: {
    ...mapGetters('loading', ['isSomeLoading']),
    ...mapState('recipes', ['draft']),
    isLoading() {
      return this.isSomeLoading([LOADING_KEY.FETCH_INVENTORY_TRANSFER_DESTINATIONS]);
    },
    currentStepComponent() {
      return steps[this.currentStep].component;
    },
    currentStepPageTitle() {
      if (this.draft.id) {
        return steps[this.currentStep].pageTitleUpdate || steps[this.currentStep].pageTitle;
      }
      return steps[this.currentStep].pageTitle;
    },
    currentStepPageInfo() {
      if (this.draft.type === 'ingredient' && this.currentStep === RECIPE_STEP_2) {
        return steps[this.currentStep].pageInfoIngredient || steps[this.currentStep].pageInfo;
      }
      return steps[this.currentStep].pageInfo;
    },
    nextCta() {
      if (this.draft.id) {
        return steps[this.currentStep].nextCtaUpdate || steps[this.currentStep].nextCta;
      }
      return steps[this.currentStep].nextCta;
    },
    wizardTitle() {
      return this.draft.id ? 'Edit Item' : 'Add New Item';
    },
  },
  methods: {
    ...mapMutations('recipes', ['CLEAR_DRAFT']),
    clearState() {
      this.CLEAR_DRAFT();
    },
    onWizardExit() {
      this.clearState();
      return this.$router.push({ name: 'venue-recipes' });
    },
    onStepBack(step) {
      if (step !== undefined && step >= RECIPE_STEP_1 && step <= RECIPE_STEP_3) {
        this.currentStep = step;
        return;
      }

      if (this.currentStep <= RECIPE_STEP_1) return;

      this.currentStep -= 1;
    },
    onStepCompleted() {
      if (this.currentStep >= RECIPE_STEP_3) {
        return;
      }

      this.nextDisabled = true;
      this.currentStep += 1;
    },
  },
  created() {
    this.currentStep = RECIPE_STEP_1;
  },
  beforeDestroy() {
    this.clearState();
  },
};
</script>

<template>
  <ez-wizard
    @exit="onWizardExit"
    @back="goToPreviousStep"
    :extraWide="[RECIPE_STEP_2, RECIPE_STEP_3].includes(currentStep)"
  >
    <template #title>{{ wizardTitle }}</template>
    <template #nav>
      <ul>
        <li
          :class="{
            'ez-step-active': currentStep === RECIPE_STEP_1,
            'ez-step-completed': currentStep > RECIPE_STEP_1,
          }"
        >
          1. Info
        </li>
        <li
          :class="{
            'ez-step-active': currentStep === RECIPE_STEP_2,
            'ez-step-completed': currentStep > RECIPE_STEP_2,
          }"
        >
          2. Products
        </li>
      </ul>
    </template>
    <template #actions>
      <ez-button :disabled="nextDisabled" @click="goToNextStep">
        {{ nextCta }}
      </ez-button>
    </template>
    <template #prevStep>
      <ez-button
        v-if="[RECIPE_STEP_2, RECIPE_STEP_3].includes(currentStep)"
        type="link"
        formType="button"
        @click="goToPreviousStep"
      >
        <font-awesome-icon icon="arrow-left" />
        <span>Back</span>
      </ez-button>
    </template>
    <template #pageTitle>{{ currentStepPageTitle }}</template>
    <template #pageInfo>
      <div v-html="currentStepPageInfo"></div>
    </template>
    <component :is="currentStepComponent" @stepCompleted="onStepCompleted" @stepBack="onStepBack" />
    <ez-loader :show="isLoading">
      {{ loadingMsg }}
    </ez-loader>
  </ez-wizard>
</template>
